import type { CaseForPharmacy } from "@omony/domain";
import { Banner, Button, ConfirmDialog } from "@omony/ui";
import { Send, Undo2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { EMPTY } from "../../shared/format.js";
import { useCaseAction, useFeatures } from "../data.js";
import { Spacer, StepFrame, useT } from "./common.js";
import { DeviceTest } from "./DeviceTest.js";
import styles from "./flow.module.css";
import { VitalsPanel } from "./VitalsPanel.js";

/** Abweichung der Geräteuhr von der Serverzeit (Timer sollen nicht springen) */
export function useServerNow(tickMs = 1000) {
  const f = useFeatures();
  const offset = f.data ? Date.parse(f.data.serverTime) - f.dataUpdatedAt : 0;
  const [now, setNow] = useState(() => Date.now() + offset);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() + offset), tickMs);
    return () => window.clearInterval(id);
  }, [offset, tickMs]);
  return now;
}

export const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${pad(m)}:${pad(s % 60)}`;
};

/** Triagiert: Gerätetest, dann an die Warteschlange übermitteln (T5) */
export function QueueStep({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const queue = useCaseAction<{ version: number }>(c.id, "queue");
  return (
    <StepFrame
      eyebrow={t("console.step.warten")}
      title={t("console.queue.title")}
      lead={t("console.queue.lead")}
      actions={
        <>
          <Spacer />
          <Button
            size="lg"
            icon={<Send />}
            onPress={() => queue.mutate({ version: c.version })}
            isDisabled={queue.isPending}
          >
            {t("console.queue.submit")}
          </Button>
        </>
      }
    >
      {c.triage ? (
        <Banner
          tone={c.triage.urgency === "rot" ? "danger" : c.triage.urgency === "gelb" ? "warning" : "success"}
        >
          {t("console.queue.triage", { urgency: t(`urgency.${c.triage.urgency}`) })}
        </Banner>
      ) : null}
      <DeviceTest />
      <ErrorBanner error={queue.error} />
    </StepFrame>
  );
}

/** Wartend: Nummer, Wartezeit (Serverzeit), zurückziehen; Vitalwerte schon jetzt möglich (A16) */
export function WaitingStep({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const features = useFeatures();
  const now = useServerNow();
  const [confirm, setConfirm] = useState(false);
  const withdraw = useCaseAction<{ version: number }>(c.id, "withdraw");
  const since = c.times.queuedAt ? Date.parse(c.times.queuedAt) : now;
  const warnAfter = (features.data?.rules.queueWarnMinutes ?? 15) * 60_000;
  const long = now - since > warnAfter;
  return (
    <StepFrame
      eyebrow={t("console.step.warten")}
      title={t("console.wait.title")}
      lead={t("console.wait.lead")}
      actions={
        <>
          <Button variant="secondary" icon={<Undo2 />} onPress={() => setConfirm(true)}>
            {t("console.wait.withdraw")}
          </Button>
        </>
      }
    >
      <div className={styles.waitGrid}>
        <div className={styles.bigStat}>
          <span className={styles.small}>{t("console.wait.number")}</span>
          <span className={styles.bigValue}>{c.signageNumber ?? EMPTY}</span>
        </div>
        <div className={styles.bigStat}>
          <span className={styles.small}>{t("console.wait.time")}</span>
          <span className={styles.bigValue} role="timer" aria-live="off">
            {mmss(now - since)}
          </span>
        </div>
      </div>
      {long ? (
        <Banner tone="warning">{t("console.wait.long", { minutes: Math.round(warnAfter / 60_000) })}</Banner>
      ) : null}
      <VitalsPanel c={c} />
      <ErrorBanner error={withdraw.error} />
      <ConfirmDialog
        isOpen={confirm}
        onOpenChange={setConfirm}
        title={t("console.wait.withdrawTitle")}
        confirmLabel={t("console.wait.withdraw")}
        onConfirm={() => withdraw.mutate({ version: c.version })}
      >
        {t("console.wait.withdrawText")}
      </ConfirmDialog>
    </StepFrame>
  );
}
